import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../../common/Input'
import { Button } from '../../common/Button'
import { Checkbox } from '../../common/Checkbox'
import { StepHeader } from '../StepHeader'

interface CommunicationConsentStepProps {
  initialSmsConsent?: boolean
  initialEmailConsent?: boolean
  initialCaregiverPhone?: string
  initialCaregiverEmail?: string
  onNext: (data: {
    smsConsent: boolean
    emailConsent: boolean
    caregiverPhone?: string
    caregiverEmail?: string
  }) => void
  onBack: () => void
  onSkip: () => void
}

/**
 * Communication consent step - SMS and email notifications (FR-021, FR-022)
 * HIPAA-compliant consent tracking with timestamps
 */
export function CommunicationConsentStep({
  initialSmsConsent = false,
  initialEmailConsent = false,
  initialCaregiverPhone = '',
  initialCaregiverEmail = '',
  onNext,
  onBack,
  onSkip,
}: CommunicationConsentStepProps) {
  const { t } = useTranslation()
  const [smsConsent, setSmsConsent] = useState(initialSmsConsent)
  const [emailConsent, setEmailConsent] = useState(initialEmailConsent)
  const [caregiverPhone, setCaregiverPhone] = useState(initialCaregiverPhone)
  const [caregiverEmail, setCaregiverEmail] = useState(initialCaregiverEmail)
  const [phoneError, setPhoneError] = useState('')
  const [emailError, setEmailError] = useState('')

  const handleNext = () => {
    // If SMS consent is enabled, phone number is required
    if (smsConsent) {
      const digits = caregiverPhone.replace(/\D/g, '')
      if (digits.length === 0) {
        setPhoneError(t('errors.required'))
        return
      }
      if (digits.length !== 10) {
        setPhoneError('Please enter a valid 10-digit US phone number')
        return
      }
    }

    // If email consent is enabled, email is required
    if (emailConsent) {
      if (!caregiverEmail.trim()) {
        setEmailError(t('errors.required'))
        return
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(caregiverEmail)) {
        setEmailError('Please enter a valid email address')
        return
      }
    }

    // Format phone as E.164 if provided
    const formattedPhone =
      smsConsent && caregiverPhone ? `+1${caregiverPhone.replace(/\D/g, '')}` : undefined

    onNext({
      smsConsent,
      emailConsent,
      caregiverPhone: formattedPhone,
      caregiverEmail: emailConsent ? caregiverEmail.trim() : undefined,
    })
  }

  const handleSkip = () => {
    onSkip()
  }

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').substring(0, 10)
    if (digits.length === 0) return ''
    if (digits.length <= 3) return `(${digits}`
    if (digits.length <= 6) return `(${digits.slice(0, 3)})${digits.slice(3)}`
    return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    setCaregiverPhone(formatted)
    if (phoneError) setPhoneError('')
  }

  const containerStyles = {
    fontFamily: 'system-ui, -apple-system, sans-serif',
  }

  const descriptionStyles = {
    fontSize: '14px',
    color: '#666',
    marginBottom: '1.5rem',
    lineHeight: 1.5,
  }

  const infoBannerStyles = {
    padding: '16px',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    marginBottom: '1.5rem',
  }

  const infoBannerTitleStyles = {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1e40af',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  }

  const infoBannerTextStyles = {
    fontSize: '14px',
    color: '#1e40af',
    lineHeight: 1.6,
  }

  const consentSectionStyles = {
    marginBottom: '1.5rem',
    padding: '16px',
    backgroundColor: '#fefefe',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
  }

  const legalTextStyles = {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: 1.6,
    marginTop: '1.5rem',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '6px',
    borderLeft: '3px solid #9ca3af',
  }

  const skipNoteStyles = {
    fontSize: '14px',
    color: '#666',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
  }

  return (
    <div style={containerStyles}>
      <StepHeader type="caregiver" title="How can Little Sister support you?" />

      <p style={descriptionStyles}>Little Sister is your teammate in caring for your loved one</p>

      {/* Info Banner */}
      <div style={infoBannerStyles}>
        <div style={infoBannerTitleStyles}>
          <span>🤝</span>
          <span>Your Care Support Partner</span>
        </div>
        <p style={infoBannerTextStyles}>
          Little Sister is here to keep you in the loop—sending you updates about daily check-ins,
          medication reminders, and important alerts. We're your partner in providing care, so you
          can stay connected without having to worry.
        </p>
        <p style={{ ...infoBannerTextStyles, marginTop: '12px', fontWeight: 500 }}>
          How would you prefer to stay updated? Choose text, email, or both.
        </p>
      </div>

      {/* SMS Consent */}
      <div style={consentSectionStyles}>
        <Checkbox
          label="📱 Text messages"
          description="Get real-time updates via SMS. By selecting this, you consent to receive automated text messages from Little Sister. Message and data rates may apply."
          checked={smsConsent}
          onChange={e => setSmsConsent(e.target.checked)}
        />

        {smsConsent && (
          <div style={{ marginTop: '1rem' }}>
            <Input
              label="Your mobile phone number"
              placeholder="(555)123-4567"
              type="tel"
              value={caregiverPhone}
              onChange={handlePhoneChange}
              error={phoneError}
              prefix="+1"
              helpText="Standard SMS rates apply. You can opt out anytime by replying STOP."
            />
          </div>
        )}
      </div>

      {/* Email Consent */}
      <div style={consentSectionStyles}>
        <Checkbox
          label="✉️ Email"
          description="Receive updates and summaries via email. By selecting this, you consent to receive email communications from Little Sister."
          checked={emailConsent}
          onChange={e => setEmailConsent(e.target.checked)}
        />

        {emailConsent && (
          <div style={{ marginTop: '1rem' }}>
            <Input
              label="Your email address"
              placeholder="your.email@example.com"
              type="email"
              value={caregiverEmail}
              onChange={e => {
                setCaregiverEmail(e.target.value)
                if (emailError) setEmailError('')
              }}
              error={emailError}
              helpText="We'll send notifications and summaries to this email. You can use a different email than your login."
            />
          </div>
        )}
      </div>

      {/* Legal Notice */}
      <div style={legalTextStyles}>
        <strong>Legal Consent & Privacy:</strong> By checking the boxes above, you provide your
        express written consent to receive communications from Little Sister. This consent is
        voluntary and you may withdraw it at any time through your account settings or by contacting
        us. We respect your privacy and will never share your contact information with third
        parties. All communications and data are protected in compliance with HIPAA regulations.
      </div>

      {/* Skip notification */}
      {!smsConsent && !emailConsent && (
        <div style={skipNoteStyles}>ℹ️ {t('onboarding.consent.continueWithout')}</div>
      )}

      {/* Navigation buttons */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Button variant="outline" onClick={onBack} style={{ flex: '1 1 150px' }}>
          {t('onboarding.navigation.back')}
        </Button>
        {!smsConsent && !emailConsent && (
          <Button variant="secondary" onClick={handleSkip} style={{ flex: '1 1 150px' }}>
            {t('onboarding.navigation.skip')}
          </Button>
        )}
        <Button
          onClick={handleNext}
          style={{ flex: '1 1 150px' }}
          disabled={!smsConsent && !emailConsent}
        >
          {t('onboarding.navigation.next')}
        </Button>
      </div>
    </div>
  )
}
